import React, { useEffect, useState } from "react";

// material-ui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import { openSnackbar } from "../../../../../api/snackbar";

// types
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";
import useAuth from "../../../../../hooks/useAuth";
import { UserList } from "../../../../../types/customer";
import { Role, ServerErrorsType } from "../../../../../types/User";

// constant
const getInitialValues = (companyAccount: UserList | null) => {
  const newCompanyAccount = {
    user_name: "",
    password: "",
    email: "",
    mobile_number: "",
    account_status: false,
    account_language: "AR",
    subscribe_to_mailing_list: false,
    account_type: "COMPANY",
    role_id: "",
  };
  if (companyAccount) {
    return {
      ...newCompanyAccount,
      ...companyAccount,
    };
  }

  return newCompanyAccount;
};

// ==============================|| COMPANY ACCOUNT ADD / EDIT - FORM ||============================== //

const FormCompanyAccountAdd = ({
  open,
  companyAccount,
  loading,
  setLoading,
  handleClose,
  setResult,
  fetchData,
}: {
  open: boolean;
  loading: boolean;
  setLoading: any;
  companyAccount: UserList | null;
  handleClose: () => void;
  setResult: any;
  fetchData: () => void;
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [serverErrors, setServerErrors] = useState<ServerErrorsType>({});

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await USERAXIOS.get("/roles");
        setRoles(response.data.data || []);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    if (open) {
      fetchRoles();
    }
  }, [open]);

  const CompanyAccountSchema = Yup.object().shape({
    user_name: Yup.string().required("User Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile_number: Yup.string().required("Mobile Number is required"),
    account_status: Yup.boolean().required("Account Status is required"),
    account_language: Yup.string().required("Account Language is required"),
    role_id: Yup.string().required("Role ID is required"),
    password: companyAccount
      ? Yup.string()
      : Yup.string()
          .required("Password is required")
          .min(6, "Password must be at least 6 characters"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(companyAccount),
    validationSchema: CompanyAccountSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const companyAccountData = {
          user_name: values.user_name,
          password: values.password,
          email: values.email,
          mobile_number: String(values.mobile_number),
          account_status: values.account_status ? "ACTIVE" : "INACTIVE",
          account_language: values.account_language,
          subscribe_to_mailing_list: values.subscribe_to_mailing_list ? 1 : 0,
          account_type: "COMPANY",
          role_id: Number(values.role_id),
        };

        if (companyAccount) {
          const response = await USERAXIOS.post(
            `/users/${companyAccount.id}`,
            companyAccountData
          );
          const updatedCompanyAccount = response.data.data;

          if (updatedCompanyAccount) {
            setResult((prev) =>
              prev.map((company) =>
                company.id === updatedCompanyAccount.id
                  ? updatedCompanyAccount
                  : company
              )
            );

            openSnackbar({
              open: true,
              message: "Company Account Updated Successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(`/users`, companyAccountData);

          const newCompanyAccount = response.data.data;

          if (newCompanyAccount) {
            setResult((prev) => [...prev, newCompanyAccount]);

            openSnackbar({
              open: true,
              message: "Company Account Added Successfully!",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          setServerErrors(error.response.data.message);
        }
        console.error("Error submitting form:", error);
        setSubmitting(false);
        openSnackbar({
          open: true,
          message: "Error saving company account",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle
            color="primary"
            style={{
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "bold",
              marginBottom: "10px",
            }}
          >
            {companyAccount ? (
              <FormattedMessage id="Edit Company Account" />
            ) : (
              <FormattedMessage id="New Company Account" />
            )}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="user-name">
                    <FormattedMessage id="User Name" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="user-name"
                    placeholder={intl.formatMessage({
                      id: "Enter user name",
                      defaultMessage: "Enter user name",
                    })}
                    {...getFieldProps("user_name")}
                    error={Boolean(
                      (touched.user_name && errors.user_name) ||
                        (serverErrors && serverErrors.user_name)
                    )}
                    helperText={
                      (touched.user_name && errors.user_name) ||
                      (serverErrors && serverErrors.user_name
                        ? serverErrors.user_name.join(" ")
                        : "")
                    }
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="email">
                    <FormattedMessage id="Email" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="email"
                    type="email"
                    placeholder={intl.formatMessage({
                      id: "Enter email",
                      defaultMessage: "Enter email",
                    })}
                    {...getFieldProps("email")}
                    error={Boolean(
                      (touched.email && errors.email) ||
                        (serverErrors && serverErrors.email)
                    )}
                    helperText={
                      (touched.email && errors.email) ||
                      (serverErrors && serverErrors.email
                        ? serverErrors.email.join(" ")
                        : "")
                    }
                    inputProps={{
                      pattern: "[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$",
                    }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="password">
                    <FormattedMessage id="Password" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="password"
                    type="password"
                    placeholder={intl.formatMessage({
                      id: "Enter password",
                      defaultMessage: "Enter password",
                    })}
                    {...getFieldProps("password")}
                    error={Boolean(
                      (touched.password && errors.password) ||
                        (serverErrors && serverErrors.password)
                    )}
                    helperText={
                      (touched.password && errors.password) ||
                      (serverErrors && serverErrors.password
                        ? serverErrors.password.join(" ")
                        : "")
                    }
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="mobile-number">
                    <FormattedMessage id="Mobile Number" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="mobile-number"
                    type="number"
                    placeholder={intl.formatMessage({
                      id: "Enter mobile number",
                      defaultMessage: "Enter mobile number",
                    })}
                    {...getFieldProps("mobile_number")}
                    error={Boolean(
                      (touched.mobile_number && errors.mobile_number) ||
                        (serverErrors && serverErrors.mobile_number)
                    )}
                    helperText={
                      (touched.mobile_number && errors.mobile_number) ||
                      (serverErrors && serverErrors.mobile_number
                        ? serverErrors.mobile_number.join(" ")
                        : "")
                    }
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="account-language">
                    <FormattedMessage id="Account Language" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl fullWidth>
                    <Select
                      {...getFieldProps("account_language")}
                      input={<OutlinedInput />}
                    >
                      <MenuItem value="AR">
                        <FormattedMessage id="Arabic" />
                      </MenuItem>
                      <MenuItem value="EN">
                        <FormattedMessage id="English" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="role-id">
                    <FormattedMessage id="Role" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Autocomplete
                    options={roles}
                    getOptionLabel={(option) => option.name}
                    value={
                      roles.find(
                        (role) => role.id.toString() === formik.values.role_id
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue(
                        "role_id",
                        newValue ? newValue.id.toString() : ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={intl.formatMessage({
                          id: "Select role",
                          defaultMessage: "Select role",
                        })}
                        error={Boolean(touched.role_id && errors.role_id)}
                        helperText={touched.role_id && errors.role_id}
                      />
                    )}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(formik.values.account_status)}
                        onChange={(e) =>
                          setFieldValue("account_status", e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Typography>
                        <FormattedMessage id="Account Status" />{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Typography>
                    }
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.subscribe_to_mailing_list}
                        onChange={(e) =>
                          setFieldValue(
                            "subscribe_to_mailing_list",
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    }
                    label={<FormattedMessage id="Subscribe to Mailing List" />}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="center" alignItems="center">
              <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                <Grid item md={2} />
                <Grid item md={4}>
                  {companyAccount ? (
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage id="Update" />
                    </Button>
                  ) : (
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage id="Submit" />
                    </Button>
                  )}
                </Grid>
                <Grid item md={4}>
                  <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                </Grid>
                <Grid item md={2} />
              </Stack>
            </Grid>
          </DialogActions>
        </Form>
      </FormikProvider>
    </ReusableModal>
  );
};

export default FormCompanyAccountAdd;
